import type {Relation} from "typeorm";
import {Column, Entity, ManyToOne, OneToMany} from "typeorm";
import {BaseEntity} from "@/modules/base/entity/BaseEntity";
import {Max, Min} from "class-validator";
import {Country} from "@/modules/base/entity";
import {Team} from "@/modules/team/entity";
import {PositionEnum} from "@/modules/base/enums";
import {YouthPlayerNegotiation} from '.';
import {CanNegotiateInterface} from "@/modules/diplomacy/interfaces";
import {CanBeRevealed} from "@/modules/youth/interfaces";

@Entity()
export class YouthPlayer extends BaseEntity implements CanNegotiateInterface, CanBeRevealed<YouthPlayer> {
  @ManyToOne(() => Country, {eager: true})
  country: Relation<Country>;

  @ManyToOne(() => Team, team => team.youthPlayers)
  team: Relation<Team>;

  @ManyToOne(() => Team, team => team.recruitedYouthPlayers)
  recruitedBy?: Relation<Team>;

  @OneToMany(() => YouthPlayerNegotiation, r => r.target)
  negotiations: YouthPlayerNegotiation[];

  @Column({type: "integer", unsigned: true})
  patience: number;

  @Column({type: "integer", unsigned: true})
  greediness: number;

  @Column({type: "integer", unsigned: true})
  resentfulness: number;

  @Column({type: "integer", unsigned: true})
  baseMonthlyPrice: number;

  @Column({type: "integer", unsigned: true})
  baseOncePrice: number;

  @Column({type: 'varchar'})
  name: string;

  @Column({type: 'float'})
  @Min(0)
  @Max(100)
  revelation = 0;

  @Column({type: 'integer'})
  @Min(1)
  @Max(100)
  skill: number;

  @Column({type: 'integer'})
  @Min(1)
  @Max(100)
  talent: number;

  @Column({type: 'integer'})
  @Min(8)
  @Max(21)
  age: number;

  @Column({type: 'integer', nullable: true})
  @Min(1)
  wage?: number;

  @Column({type: 'integer', nullable: true})
  @Min(1)
  marketValue?: number;

  @Column({type: 'varchar', enum: PositionEnum})
  position: PositionEnum;

  get isFullyRevealed() {
    return this.revelation === 100;
  }

  get shortName() {
    const lastName = this.name.split(' ').pop();

    return `${this.name[0]}. ${lastName}`;
  }

  get revealedTalent() {
    if (!this.canSee('talent')) {
      return null;
    }

    return this.talent;
  }

  canSee(attr: keyof YouthPlayer): boolean {
    switch (attr) {
      case 'country':
        return this.revelation >= 10;
      case 'name':
        return this.revelation >= 10;
      case 'age':
        return this.revelation >= 25;
      case 'position':
        return this.revelation >= 50;
      case 'skill':
        return this.revelation >= 75;
      case 'talent':
        return this.revelation >= 100;
    }

    return true;
  }
}
